<template>
  <div id="app" class="container-fluid">
    <full_mental_health_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading records...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-brain"></i> Mental Health Assessments</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Assessment
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No records yet for this visit.
          </div>
          <div v-else class="rec-list">
            <div class="rec-card" v-for="record in records" :key="record.id">
              <div class="rec-card-header">
                <span class="rec-date">{{ formatDateTime(record.assessed_at) }}</span>
                <div class="rec-actions">
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="rec-body">
                <div class="rec-row">
                  <span v-if="record.mood"><strong>Mood:</strong> {{ record.mood }}</span>
                  <span v-if="record.affect"><strong>Affect:</strong> {{ record.affect }}</span>
                  <span v-if="record.insight"><strong>Insight:</strong> {{ record.insight }}</span>
                  <span v-if="record.risk_level"><strong>Risk Level:</strong> {{ record.risk_level }}</span>
                </div>
                <p v-if="record.provisional_diagnosis" class="rec-text"><strong>Diagnosis:</strong> {{ record.provisional_diagnosis }}</p>
                <p v-if="record.management_plan" class="rec-text"><strong>Management Plan:</strong> {{ record.management_plan }}</p>
                <p v-if="record.safety_plan" class="rec-text"><strong>Safety Plan:</strong> {{ record.safety_plan }}</p>
                <p v-if="record.notes" class="rec-text"><strong>Notes:</strong> {{ record.notes }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <MentalHealthAssessmentModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_mental_health_head from "./full_mental_health_head.vue";
import MentalHealthAssessmentModal from "./MentalHealthAssessmentModal.vue";
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "MentalHealthAssessmentView",
  components: { full_mental_health_head, MentalHealthAssessmentModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`mental_health_assessments/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load records";
        }
      } catch (err) {
        console.error("Error fetching records:", err);
        this.error = "An error occurred while loading records. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this record?")) return;
      try {
        const response = await generalApiRequest(`mental_health_assessments/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete record");
        }
      } catch (error) {
        console.error("Error deleting record:", error);
        alert("Failed to delete record. Please try again.");
      }
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #34399b; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #34399b; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #34399b; }
.btn-edit-header { background: linear-gradient(135deg, #4c51bf 0%, #34399b 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.rec-list { display: flex; flex-direction: column; gap: 14px; }
.rec-card { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.rec-card-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; }
.rec-date { font-size: 12px; color: #6c757d; font-weight: 600; }
.rec-actions { display: flex; gap: 8px; }
.btn-icon { background: none; border: none; color: #34399b; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
.rec-body { padding: 12px 14px; }
.rec-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #495057; margin-bottom: 8px; }
.rec-text { font-size: 13px; color: #495057; margin: 4px 0; }
</style>
